/**
 * Дедуп пушей по id с хранением в IndexedDB.
 *
 * In-memory-мапа не переживает рестарт Service Worker (браузер выгружает SW на idle),
 * поэтому состояние хранится в IDB. Claim атомарен: в одной readwrite-транзакции
 * чистятся устаревшие по TTL записи, затем `add` падает на дубле ключа (`ConstraintError`)
 * и тем самым блокирует повторный показ. TTL-чистка встроена в каждый claim — отдельных
 * таймеров не нужно (SW их не держит), а объём хранилища ограничен числом пушей за TTL.
 *
 * Контракт: `claimPushId` никогда не бросает и никогда не подавляет пуш из-за сбоя IDB —
 * при любой ошибке или недоступности возвращается `true` (показать). `false` — только
 * настоящий дубль.
 */
import { ensureStore, SW_PUSH_IDS_DB } from 'serviceWorker/swIdbSchema';

// Схема — в общем модуле: этот же стор со страницы чистит app.webPush.clearDedup().
const { name: DB_NAME, store: STORE, version: DB_VERSION } = SW_PUSH_IDS_DB;

// Дубль одного пуша через разные каналы приходит за секунды — 10 минут с запасом.
const TTL_MS = 10 * 60 * 1000;

let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
    if (typeof indexedDB === 'undefined') {
        return Promise.resolve(null);
    }

    if (dbPromise) {
        return dbPromise;
    }

    dbPromise = new Promise((resolve) => {
        try {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = () => {
                ensureStore(request.result, SW_PUSH_IDS_DB);
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => resolve(null);
        } catch {
            resolve(null);
        }
    });

    return dbPromise;
}

/**
 * Заклеймить id как показанный. Вернёт `true`, если id ещё не показывался (можно
 * показывать), и `false` — при дубле (другая копия уже показала). Атомарно через `add`
 * в readwrite-транзакции; перед add чистятся устаревшие по TTL записи.
 */
export function claimPushId(id: string): Promise<boolean> {
    return openDb()
        .then((db) => {
            if (!db) {
                return true; // IDB недоступен — не блокируем показ
            }

            return new Promise<boolean>((resolve) => {
                try {
                    const tx = db.transaction(STORE, 'readwrite');
                    const store = tx.objectStore(STORE);
                    const cutoff = Date.now() - TTL_MS;

                    // чистим устаревшие записи; claim — после завершения курсора,
                    // в той же транзакции
                    const cursorReq = store.openCursor();

                    cursorReq.onsuccess = () => {
                        const cursor = cursorReq.result;

                        if (cursor) {
                            if (typeof cursor.value === 'number' && cursor.value < cutoff) {
                                cursor.delete();
                            }

                            cursor.continue();
                        } else {
                            // claim: add падает на дубле ключа (ConstraintError)
                            const addReq = store.add(Date.now(), id);

                            addReq.onsuccess = () => resolve(true);
                            addReq.onerror = () => resolve(addReq.error?.name !== 'ConstraintError');
                        }
                    };

                    tx.onerror = () => resolve(true);
                    tx.onabort = () => resolve(true);
                } catch {
                    resolve(true); // не блокируем показ при сбое
                }
            });
        })
        .catch(() => true); // на любой случай — не блокируем
}

/**
 * CLOUDWEB-21024: сбросить клеймы показанных пушей (`app.webPush.clearDedup()`).
 *
 * Модуль не тянет SW-специфичных API, поэтому команду зовут обе консоли — и страницы, и
 * воркера: IndexedDB общая на origin, второй реализации не нужно.
 *
 * Нужно для отладки: без этого повторная отправка пуша с тем же `id` подавляется до истечения
 * TTL, и приходится либо ждать десять минут, либо каждый раз менять `id` в запросе.
 * Как и `claimPushId`, ничего не бросает — при недоступном IDB просто `false`.
 */
export async function clearShownPushIds(): Promise<boolean> {
    try {
        const db = await openDb();

        if (!db) {
            return false;
        }

        return await new Promise<boolean>((resolve) => {
            try {
                const tx = db.transaction(STORE, 'readwrite');

                tx.objectStore(STORE).clear();
                tx.oncomplete = () => resolve(true);
                tx.onerror = () => resolve(false);
                tx.onabort = () => resolve(false);
            } catch {
                resolve(false);
            }
        });
    } catch {
        return false;
    }
}
